import { useState } from 'react';
import { Badge, DefenseCard, ShipCard, Tabs, ViewHeader } from '@spacegame/ui';
import { DEFENSE_KEYS, SHIP_KEYS, UNITS, buildingLevel } from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';
import { unitCard } from '../game/selectors';
import { entityArt } from '../components/EntityArt';

const BATCHES = [
  { id: '1', label: '×1' },
  { id: '5', label: '×5' },
  { id: '25', label: '×25' },
];

/** Werft (ships) and Abwehr (defense) share the batch-build layout. */
export function HangarView({ kind }: { kind: 'ship' | 'defense' }) {
  const { game, activePlanetIndex } = useGame();
  const actions = useGameActions();
  const [batchId, setBatchId] = useState('1');
  const batch = Number(batchId);
  const planetIndex = activePlanetIndex;
  const planet = game.planets[planetIndex]!;

  const keys = kind === 'ship' ? SHIP_KEYS : DEFENSE_KEYS;
  const nano = planetIndex === 0 ? buildingLevel(game, 'nanoFactory') : 0;
  const shipyard = buildingLevel(game, 'shipyard', planetIndex);

  return (
    <>
      {game.planets.length > 1 ? (
        <Tabs
          variant="enclosed"
          className="mb-3"
          value={String(planetIndex)}
          onChange={(id) => actions.setActivePlanetIndex(Number(id))}
          items={game.planets.map((p, i) => ({
            id: String(i),
            label: i === 0 ? `${p.name} (Heimat)` : p.name,
          }))}
        />
      ) : null}
      <ViewHeader
        eyebrow={
          kind === 'ship'
            ? `Werft Stufe ${shipyard}${nano > 0 ? ` · Nano-Fabrikationszentrum Stufe ${nano}` : ''}`
            : planet.name
        }
        title={kind === 'ship' ? 'Flottenhangar' : 'Verteidigungssysteme'}
        description={
          kind === 'defense'
            ? 'Stationäre Defensivstrukturen schützen Rohstoffe und Infrastruktur vor Überfällen und Kometen.'
            : undefined
        }
        actions={
          <div className="flex items-center gap-2">
            {game.event?.type === 'comet' ? (
              <Badge accent="danger" pulse>Komet im Anflug</Badge>
            ) : null}
            <Tabs variant="enclosed" items={BATCHES} value={batchId} onChange={setBatchId} />
          </div>
        }
      />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {keys.map((key) => {
          const card = unitCard(game, key, batch, game.lastTick, planetIndex);
          const shared = {
            name: card.label,
            art: entityArt(UNITS[key].sprite, card.label),
            owned: card.owned,
            state: card.state === 'maxed' ? ('maxed' as const) : card.state,
            cost: card.cost,
            available: game.resources,
            duration: card.durationSeconds,
            requirement: card.requirement,
            onBuild: () => actions.buildUnits(key, batch, planetIndex),
          };
          return card.kind === 'ship' ? (
            <ShipCard key={key} role={card.role} stats={card.stats} {...shared} />
          ) : (
            <DefenseCard
              key={key}
              special={card.role}
              stats={{ hull: card.stats.hull, shield: card.stats.shield, damage: card.stats.damage }}
              {...shared}
            />
          );
        })}
      </div>
    </>
  );
}
